import React, { createContext, useContext, useEffect } from 'react';
import { useSiteContent } from './SiteContentContext';

const DesignContext = createContext();

export const useDesign = () => {
  const context = useContext(DesignContext);
  if (!context) {
    throw new Error('useDesign must be used within a DesignProvider');
  }
  return context;
};

// Default design values
const defaultDesign = {
  typography: {
    headingFont: 'Inter',
    bodyFont: 'Inter',
    headingWeight: '600',
    bodyWeight: '400',
    baseSize: '16',
    lineHeight: '1.7'
  },
  colors: {
    primary: '#1e293b',
    primaryLight: '#334155',
    secondary: '#64748b',
    accent: '#0f172a',
    background: '#ffffff',
    backgroundAlt: '#f8fafc',
    text: '#1e293b',
    textMuted: '#64748b',
    border: '#e2e8f0',
    success: '#22c55e',
    error: '#ef4444'
  },
  spacing: {
    sectionDesktop: '96',
    sectionMobile: '48',
    containerPadding: '24',
    cardPadding: '24',
    buttonPaddingX: '32',
    buttonPaddingY: '14'
  },
  buttons: {
    borderRadius: '9999',
    primaryBg: '#1e293b',
    primaryText: '#ffffff',
    primaryHover: '#334155',
    secondaryBg: '#ffffff',
    secondaryText: '#1e293b',
    secondaryBorder: '#e2e8f0',
    secondaryHover: '#f8fafc'
  },
  cards: {
    borderRadius: '16',
    shadow: '0 1px 3px rgba(0,0,0,0.05)',
    border: '#f1f5f9'
  }
};

export const DesignProvider = ({ children }) => {
  const { siteContent } = useSiteContent();
  
  // Merge site content design with defaults
  const design = siteContent?.design ? {
    typography: { ...defaultDesign.typography, ...siteContent.design.typography },
    colors: { ...defaultDesign.colors, ...siteContent.design.colors },
    spacing: { ...defaultDesign.spacing, ...siteContent.design.spacing },
    buttons: { ...defaultDesign.buttons, ...siteContent.design.buttons },
    cards: { ...defaultDesign.cards, ...siteContent.design.cards }
  } : defaultDesign;

  // Apply CSS variables to document root
  useEffect(() => {
    const root = document.documentElement;
    
    // Typography
    root.style.setProperty('--font-heading', design.typography.headingFont);
    root.style.setProperty('--font-body', design.typography.bodyFont);
    root.style.setProperty('--font-heading-weight', design.typography.headingWeight);
    root.style.setProperty('--font-body-weight', design.typography.bodyWeight);
    root.style.setProperty('--font-base-size', `${design.typography.baseSize}px`);
    root.style.setProperty('--line-height', design.typography.lineHeight);
    
    // Colors
    root.style.setProperty('--color-primary', design.colors.primary);
    root.style.setProperty('--color-primary-light', design.colors.primaryLight);
    root.style.setProperty('--color-secondary', design.colors.secondary);
    root.style.setProperty('--color-accent', design.colors.accent);
    root.style.setProperty('--color-background', design.colors.background);
    root.style.setProperty('--color-background-alt', design.colors.backgroundAlt);
    root.style.setProperty('--color-text', design.colors.text);
    root.style.setProperty('--color-text-muted', design.colors.textMuted);
    root.style.setProperty('--color-border', design.colors.border);
    root.style.setProperty('--color-success', design.colors.success);
    root.style.setProperty('--color-error', design.colors.error);
    
    // Spacing
    root.style.setProperty('--spacing-section-desktop', `${design.spacing.sectionDesktop}px`);
    root.style.setProperty('--spacing-section-mobile', `${design.spacing.sectionMobile}px`);
    root.style.setProperty('--spacing-container', `${design.spacing.containerPadding}px`);
    root.style.setProperty('--spacing-card', `${design.spacing.cardPadding}px`);
    root.style.setProperty('--button-padding-x', `${design.spacing.buttonPaddingX}px`);
    root.style.setProperty('--button-padding-y', `${design.spacing.buttonPaddingY}px`);
    
    // Buttons
    root.style.setProperty('--button-radius', `${design.buttons.borderRadius}px`);
    root.style.setProperty('--button-primary-bg', design.buttons.primaryBg);
    root.style.setProperty('--button-primary-text', design.buttons.primaryText);
    root.style.setProperty('--button-primary-hover', design.buttons.primaryHover);
    root.style.setProperty('--button-secondary-bg', design.buttons.secondaryBg);
    root.style.setProperty('--button-secondary-text', design.buttons.secondaryText);
    root.style.setProperty('--button-secondary-border', design.buttons.secondaryBorder);
    root.style.setProperty('--button-secondary-hover', design.buttons.secondaryHover);
    
    // Cards
    root.style.setProperty('--card-radius', `${design.cards.borderRadius}px`);
    root.style.setProperty('--card-shadow', design.cards.shadow);
    root.style.setProperty('--card-border', design.cards.border);
    
  }, [design]);

  return (
    <DesignContext.Provider value={{ design, defaultDesign }}>
      {children}
    </DesignContext.Provider>
  );
};

export default DesignContext;
